import { ChangeEvent, useEffect } from 'react';
import { DaysOfWeek } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/bulk.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { formatDate, getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import {
  BaseFieldProps,
  BasicBoolFieldProps,
  BasicFormFieldProps,
  CustomChangeEvent,
  FieldChangeEvent,
  FormFieldActionTypes,
  useForm,
} from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import {
  BACKEND_DATE_TIME_FORMAT,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  MAX_BATCH_RECIPIENTS,
  TIME_FORMAT,
} from '../constants';
import { type AttachmentFieldProps, type DaysToSendFieldProps, type DayToSend, SendType } from '../types';
import { getNextMinTime } from '../utils/get-next-min-time';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const defaultDaysToSend: DayToSend[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const validDays: DayToSend[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const convertToDaysToSend = (daysOfWeek?: DaysOfWeek): DayToSend[] => {
  if (!daysOfWeek) {
    return [];
  }

  const values: DayToSend[] = [];
  validDays.forEach((day) => {
    if (daysOfWeek[day]) {
      values.push(day);
    }
  });
  return values;
};

const convertToDaysOfWeek = (daysToSend?: readonly string[]): DaysOfWeek => {
  const daysOfWeek: DaysOfWeek = {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
  };

  if (daysToSend && Array.isArray(daysToSend)) {
    daysToSend.forEach((day) => {
      if (validDays.includes(day as DayToSend)) {
        daysOfWeek[day as DayToSend] = true;
      }
    });
  }

  return daysOfWeek;
};

const toggleDayInList = (daysToSend: readonly string[] | undefined, toggledDay: string): DayToSend[] => {
  const currentDays = (daysToSend ?? []).filter((day): day is DayToSend => validDays.includes(day as DayToSend));
  if (!validDays.includes(toggledDay as DayToSend)) {
    return currentDays;
  }

  const includesDay = currentDays.includes(toggledDay as DayToSend);
  return includesDay ? currentDays.filter((day) => day !== toggledDay) : [...currentDays, toggledDay as DayToSend];
};

export const useEmailForm = () => {
  const { t } = useTranslation('bulk-messaging');
  const {
    attachmentMediaIds,
    consent,
    locationIds,
    readOnly,
    sendDays,
    sendPerDay,
    setAttachmentMediaIds,
    setConsent,
    setLocationIds,
    setSendDays,
    setSendPerDay,
    setStartSendAt,
    setTitle,
    startSendAt,
    title,
  } = useBulkMessageEditorContext();

  const todaysDate = getTodaysDate(DATE_FORMAT);
  const { getFieldProps, seedValues, validate, values } = useForm({
    computeChangedValues: true,
    fields: {
      title: {
        type: 'text',
        value: title,
        required: true,
        placeholder: t('Name Your Campaign'),
      },
      locationIds: {
        type: 'checklist',
        value: locationIds ?? [],
        required: true,
        placeholder: t('Select Locations'),
      },
      date: {
        type: 'datePicker',
        value: formatDate(startSendAt, DATE_FORMAT),
        required: !readOnly,
        minDate: todaysDate,
        maxDate: dayjs().add(1, 'year').format(DATE_FORMAT),
      },
      time: {
        type: 'time',
        value: formatDate(startSendAt, TIME_FORMAT) || getNextMinTime(todaysDate, dayjs().hour()),
        required: !readOnly,
        hidden: !startSendAt,
      },
      sendOptions: {
        type: 'radio',
        value: sendPerDay > 0 ? SendType.SendSpecific : SendType.SendAll,
        required: !readOnly,
        hidden: !startSendAt,
      },
      sendInterval: {
        type: 'number',
        value: sendPerDay > 0 ? sendPerDay.toString() : '',
        min: 1,
        max: MAX_BATCH_RECIPIENTS, // TODO: What is the maximum number of recipients?
      },
      daysToSend: {
        type: 'checklist',
        value: sendDays ? convertToDaysToSend(sendDays) : defaultDaysToSend,
        required: !readOnly,
        hidden: sendPerDay === 0,
      },
      attachments: {
        type: 'multiselect',
        value: attachmentMediaIds,
        hidden: locationIds.length > 1,
      },
      consent: {
        type: 'checkbox',
        required: !readOnly,
        hidden: readOnly,
        value: consent,
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type !== FormFieldActionTypes.Update) return null;

      if (action.payload.name === 'date') {
        return {
          ...state,
          // TODO: Fix the time dropdown when switching between dates
          time: {
            ...state.time,
            minTime: state.date.value === todaysDate ? getNextMinTime(state.date.value as string) : '0:00',
            hidden: action.payload.value === '',
          },
          sendOptions: {
            ...state.sendOptions,
            hidden: action.payload.value === '',
          },
          daysToSend: {
            ...state.daysToSend,
            hidden: action.payload.value === '' || state.sendOptions.value !== SendType.SendSpecific,
          },
        };
      }

      if (action.payload.name === 'sendOptions') {
        return {
          ...state,
          daysToSend: {
            ...state.daysToSend,
            hidden: action.payload.value !== SendType.SendSpecific,
          },
        };
      }

      return state;
    },
  });

  // when the user changes the date or time, we need to update the startSendAt in the context
  useEffect(() => {
    // using an effect instead of imperatively updating it in the onChange handlers below because both date and time are needed to set the value (and we want to track for the `changesExist` logic)
    const newStartSendAt =
      values.date && values.time
        ? formatDate(dayjs(`${values.date} ${values.time}`, DATE_TIME_FORMAT), BACKEND_DATE_TIME_FORMAT)
        : '';
    setStartSendAt(newStartSendAt);
  }, [setStartSendAt, values.date, values.time]);

  useEffect(() => {
    if (values.sendOptions !== SendType.SendSpecific) {
      setSendDays(undefined);
      return;
    }

    // Sync form state to context. If sendDays is undefined, use the form's current value (which defaults to defaultDaysToSend)
    const mappedDays = convertToDaysOfWeek(Array.isArray(values.daysToSend) ? values.daysToSend : undefined);
    setSendDays(mappedDays);
  }, [setSendDays, values.daysToSend, values.sendOptions]);

  // custom form props to handle context updates
  // the casting needs to happen because the other components used under the hood (shared implementations) has conflicting types for the `onChange` prop
  const formProps = {
    attachments: {
      ...getFieldProps('attachments'),
      onChange: (e: CustomChangeEvent<string[]>) => {
        getFieldProps('attachments').onChange(e);
        // update attachmentMediaIds in context
        setAttachmentMediaIds(e.value);
      },
    } as AttachmentFieldProps,
    consent: {
      ...getFieldProps('consent'),
      onChange: (e: CustomChangeEvent<boolean>) => {
        getFieldProps('consent').onChange(e);
        // update consent in context
        setConsent(e.value);
      },
    } as BasicBoolFieldProps<'checkbox'>,
    date: getFieldProps('date'),
    daysToSend: {
      ...getFieldProps('daysToSend'),
      onChange: (e: CustomChangeEvent<string>) => {
        getFieldProps('daysToSend').onChange(e);

        // SelectedDaysChips emits a single toggled day; update context directly from that event.
        const newDaysToSend = toggleDayInList(values.daysToSend, e.value);
        setSendDays(convertToDaysOfWeek(newDaysToSend));
      },
    } as DaysToSendFieldProps,
    locationIds: {
      ...getFieldProps('locationIds'),
      onChange: (e?: FieldChangeEvent) => {
        getFieldProps('locationIds').onChange(e);

        // @ts-ignore design system type issue
        const value = e.value;
        const includes = locationIds.find((id) => id === value);
        const newLocationIds = includes ? locationIds.filter((id) => id !== value) : [...locationIds, value];
        setLocationIds(newLocationIds);
      },
    },
    sendInterval: {
      ...getFieldProps('sendInterval'),
      onChange: (e: CustomChangeEvent<string>) => {
        getFieldProps('sendInterval').onChange(e);
        // when the user changes the send interval, we need to update the sendPerDay in the context
        setSendPerDay((_) => (e.value ? parseInt(e.value) : 0));
      },
    } as BasicFormFieldProps<'number'>,
    sendOptions: {
      ...getFieldProps('sendOptions'),
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        getFieldProps('sendOptions').onChange(e);

        // when switching to SendAll, set sendPerDay to 0 in context
        const newSendOption = e.target.value as SendType;
        const newSendPerDay =
          newSendOption === SendType.SendAll ? 0 : values.sendInterval ? parseInt(values.sendInterval) : 0;
        setSendPerDay((_) => newSendPerDay);

        // when switching to SendAll, clear sendDays in context
        if (newSendPerDay === 0) {
          setSendDays((_) => undefined);
        }
      },
    } as Omit<BasicFormFieldProps<'radio'>, 'label'>,
    time: getFieldProps('time'),
    title: {
      ...getFieldProps('title'),
      onChange: (e) => {
        getFieldProps('title').onChange(e);
        // update title in context
        // @ts-ignore design system type issue
        setTitle((_) => e.target.value);
      },
    } as BaseFieldProps,
  };

  return {
    formProps,
    seedValues,
    validateForm: validate,
    values,
  };
};
